import { initialiseSearchSessionId } from 'actions/AnalyticsActions'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import usePrevious from 'hooks/usePrevious'
import { useContext, useEffect } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import GeoContext from 'contexts/geoContext'
import { reportLog } from 'services/userMonitoring'

// SearchResultBucket is all possible buckets for a deal in the search result
export enum SearchResultBucket {
  LE_HOTEL = 'LE',
  LE_HOTEL_NEARBY = 'LE Nearby',
  BEDBANK_HOTEL = 'Bedbank',
  BEDBANK_HOTEL_NEARBY = 'Bedbank Nearby',
  LE_TOUR = 'LE Tour',
  LE_TOUR_NEARBY = 'LE Tour Nearby',
  TTC_TOUR = 'TTC Tour',
  TTC_TOUR_NEARBY = 'TTC TOUR Nearby',
  LED_EXPERIENCE = 'LED Experience',
  REZDY_EXPERIENCE = 'Rezdy Experience',
  CRUISE_TRIP = 'Cruise Trip',
}

export interface SearchResult {
  id: string
  kind: SearchResultBucket
}

export function useGlobalSearchResultsTracking(
  globalSearchState: GlobalSearchState,
  offerListKey: string,
  trackableSearchResults: Array<SearchResult>,
  areResultsFetching: boolean,
  numberOfResultIgnoreAvailability?: number,
) {
  const {
    searchItem,
    searchVerticals,
    occupancies,
    checkinDate,
    checkoutDate,
    flexibleMonths,
    flexibleNights,
    isFlexibleDateSelected,
    searchTargetLabel,
  } = globalSearchState

  const dispatch = useAppDispatch()
  const searchSessionId = useAppSelector(state => state.analytics.search.sessions[offerListKey])
  const searchError = useAppSelector(state => state.offer.offerLists[offerListKey]?.error)
  const searchId = useAppSelector(state => state.offer.offerLists[offerListKey]?.searchId)

  useEffect(() => {
    if (offerListKey) {
      dispatch(initialiseSearchSessionId(offerListKey))
    }
  }, [dispatch, offerListKey])

  const userIsLoggedIn = useAppSelector(selectLoggedIn)
  const { currentRegionCode: regionCode } = useContext(GeoContext)

  const wasFetching = usePrevious(areResultsFetching)
  const justFinishedFetching = !areResultsFetching && wasFetching

  useEffect(() => {
    if (justFinishedFetching && searchSessionId) {
      if (searchError) {
        reportLog('Search error', {
          searchSessionId,
          offerListKey,
          searchError,
          searchState: {
            searchVerticals,
            searchItem,
            occupancies,
            checkinDate,
            checkoutDate,
            flexibleMonths,
            flexibleNights,
            isFlexibleDateSelected,
          },
        })
      }
    }
  }, [
    searchId,
    offerListKey,
    checkinDate,
    checkoutDate,
    flexibleMonths,
    flexibleNights,
    justFinishedFetching,
    occupancies,
    regionCode,
    searchItem,
    searchSessionId,
    searchVerticals,
    trackableSearchResults,
    userIsLoggedIn,
    isFlexibleDateSelected,
    numberOfResultIgnoreAvailability,
    searchTargetLabel,
    dispatch,
    searchError,
  ])
}
